<template>
    <section class="bg-gray-100 min-h-screen font-sans">

        <!-- En-tête -->
        <div class="w-full min-h-[50vh] p-6 sm:p-8 shadow-lg flex flex-col items-center justify-center text-white"
            :style="{ backgroundColor: 'var(--primary-color)' }">
            <div
                class="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 mx-auto text-white text-center md:text-left">
                <!-- Logo de l'entreprise -->
                <img :src="logoFullUrl" alt="Logo de l'entreprise"
                    class="h-28 w-28 sm:h-36 sm:w-36 object-cover rounded-full border-4 border-white" />

                <!-- Informations de l'entreprise -->
                <div class="space-y-3 text-center md:text-left text-white">
                    <h1 class="text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                        {{ entreprise?.nom }}
                    </h1>

                    <p class="text-lg sm:text-2xl font-semibold">
                        {{ entreprise?.activite }}
                    </p>

                    <!-- Version mobile : sans icône -->
                    <p class="block sm:hidden text-base font-light sm:text-lg">
                        <strong>{{ entreprise?.zone }}</strong>
                    </p>

                    <!-- Version sm et + : avec icône -->
                    <p
                        class="hidden sm:flex text-base font-light sm:text-lg items-center gap-2 justify-center md:justify-start w-full">
                        <i class="fas fa-location-dot"></i>
                        <strong>{{ entreprise?.zone }}</strong>
                    </p>

                </div>

            </div>
            <!-- Bouton téléphone + mail -->
            <div class="flex flex-col sm:flex-row justify-center gap-6 mt-10">
                <a v-if="entreprise?.telephone" :href="`tel:${entreprise.telephone}`"
                    class="bg-white hover:bg-gray-100 font-semibold py-3 px-6  inline-flex items-center justify-center shadow-md transition w-full sm:w-auto text-center rounded-lg"
                    :style="{ color: 'var(--primary-color)' }">
                    <i class="fas fa-phone mr-3"></i> {{ entreprise.telephone }}
                </a>

                <a v-if="entreprise?.email" :href="`mailto:${entreprise.email}`"
                    class="bg-white hover:bg-gray-100 font-semibold py-3 px-6  inline-flex items-center justify-center shadow-md transition w-full sm:w-auto text-center rounded-lg"
                    :style="{ color: 'var(--primary-color)' }">
                    <i class="fas fa-envelope mr-3"></i> {{ entreprise.email }}
                </a>
            </div>
        </div>

        <section class="max-w-7xl px-6 mx-auto py-8">
            <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">

                <!-- Colonne gauche : Description + Prestations -->
                <div class="lg:col-span-8 space-y-8">

                    <!-- Description -->
                    <div v-if="hasDescription"
                        class="border shadow-sm border-gray-100 text-left text-gray-800 p-6 md:p-7 bg-white rounded-2xl">
                        <h2 class="text-2xl font-bold mb-4">Description</h2>
                        <p class="text-lg leading-relaxed whitespace-pre-line">
                            {{ entreprise?.description }}
                        </p>
                    </div>



                    <!-- Prestations (si remplacement description) -->
                    <div v-if="hasPrestations" class="p-6 shadow-sm border border-gray-100 bg-white rounded-2xl">
                        <h2 class="text-2xl font-bold text-left text-gray-800 mb-6">
                            Prestations
                        </h2>

                        <ul class="space-y-4 text-lg">
                            <li v-for="(prestation, index) in prestations" :key="index" class="border-b pb-2">
                                <!-- Ligne titre + prix -->
                                <div class="flex items-center justify-between cursor-pointer"
                                    @click="prestation.description ? toggleDescription(index) : null">
                                    <div class="flex items-center">
                                        <!-- Flèche si description -->
                                        <i v-if="prestation.description" :class="[
                                            'fas mr-2 transition-transform',
                                            openDescription === index ? 'fa-chevron-down rotate-180' : 'fa-chevron-down'
                                        ]"></i>

                                        <span class="capitalize">{{ prestation.titre }}</span>
                                    </div>

                                    <span class="font-semibold ml-4">
                                        <span v-if="prestation.prix !== null">{{ prestation.prix }}€</span>
                                        <span v-else class="italic text-gray-400"></span>
                                    </span>
                                </div>

                                <!-- Description -->
                                <transition name="fade">
                                    <p v-if="openDescription === index" class="mt-2 text-gray-600 text-base pl-6">
                                        {{ prestation.description }}
                                    </p>
                                </transition>
                            </li>
                        </ul>
                    </div>


                    <!-- Réservation ou Devis -->
                    <div v-if="entreprise?.reservation_url || entreprise?.telephone || entreprise?.email || entreprise?.afficher_devis"
                        class="text-white shadow-lg py-10 text-center flex flex-col items-center space-y-6 sm:space-y-4 rounded-2xl"
                        :style="{ backgroundColor: 'var(--primary-color)' }">

                        <!-- Bloc Réservation -->
                        <template
                            v-if="(entreprise?.reservation_url || entreprise?.telephone || entreprise?.email) && !entreprise?.afficher_devis">
                            <h2 class="text-2xl sm:text-3xl font-bold">Prendre un rendez-vous</h2>
                            <p v-if="entreprise?.reservation_url"
                                class="text-base sm:text-lg max-w-md sm:max-w-lg px-6">
                                Réservez votre créneau facilement via notre plateforme de prise de rendez-vous en ligne.
                            </p>
                            <p v-else class="text-base sm:text-lg max-w-md sm:max-w-lg px-6">
                                Contactez-nous dès maintenant afin de fixer un rendez-vous ensemble.
                            </p>

                            <!-- Bouton réservation si URL dispo -->
                            <a v-if="entreprise?.reservation_url" :href="entreprise.reservation_url" target="_blank"
                                rel="noopener"
                                class="bg-white hover:opacity-90 font-semibold py-3 px-6 sm:px-8 shadow-md transition-all sm:w-auto text-center rounded-lg"
                                :style="{ color: 'var(--primary-color)' }">
                                Réserver dès maintenant
                            </a>

                            <!-- Sinon : boutons téléphone et mail -->
                            <div v-else class="flex flex-col sm:flex-row justify-center gap-6 mt-10">
                                <a v-if="entreprise?.telephone" :href="`tel:${entreprise.telephone}`"
                                    class="bg-white hover:bg-gray-100 font-semibold py-3 px-6 inline-flex items-center justify-center shadow-md transition sm:w-auto text-center rounded-lg"
                                    :style="{ color: 'var(--primary-color)' }">
                                    <i class="fas fa-phone mr-3"></i> {{ entreprise.telephone }}
                                </a>
                                <a v-if="entreprise?.email" :href="`mailto:${entreprise.email}`"
                                    class="bg-white hover:bg-gray-100 font-semibold py-3 px-6 inline-flex items-center justify-center shadow-md transition sm:w-auto text-center rounded-lg"
                                    :style="{ color: 'var(--primary-color)' }">
                                    <i class="fas fa-envelope mr-3"></i> {{ entreprise.email }}
                                </a>
                            </div>
                        </template>

                        <!-- Bloc Devis -->
                        <template v-else-if="entreprise?.afficher_devis">
                            <h2 class="text-2xl sm:text-3xl font-bold">Demander un devis</h2>
                            <p class="text-base sm:text-lg max-w-md sm:max-w-lg px-6">
                                Demandez dès maintenant votre devis personnalisé parfaitement adapté à vos besoins.
                            </p>
                            <div class="flex flex-col sm:flex-row justify-center gap-6 mt-10">
                                <a v-if="entreprise?.telephone" :href="`tel:${entreprise.telephone}`"
                                    class="bg-white hover:bg-gray-100 font-semibold py-3 px-6 inline-flex items-center justify-center shadow-md transition w-full sm:w-auto text-center rounded-lg"
                                    :style="{ color: 'var(--primary-color)' }">
                                    <i class="fas fa-phone mr-3"></i> {{ entreprise.telephone }}
                                </a>
                                <a v-if="entreprise?.email" :href="`mailto:${entreprise.email}`"
                                    class="bg-white hover:bg-gray-100 font-semibold py-3 px-6 inline-flex items-center justify-center shadow-md transition w-full sm:w-auto text-center rounded-lg"
                                    :style="{ color: 'var(--primary-color)' }">
                                    <i class="fas fa-envelope mr-3"></i> {{ entreprise.email }}
                                </a>
                            </div>
                        </template>
                    </div>

                    <!-- Réseaux sociaux -->
                    <div v-if="hasSocialLinks"
                        class="hidden md:block p-6 text-center text-gray-800 bg-white shadow-sm border border-gray-100 rounded-2xl">
                        <h2 class="text-2xl font-bold text-gray-800">Suivez-nous</h2>
                        <div class="flex flex-wrap justify-center gap-6 text-4xl py-4">
                            <a v-if="reseaux.facebook" :href="reseaux.facebook" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Facebook.png" alt="Facebook" class="h-10" />
                            </a>
                            <a v-if="reseaux.instagram" :href="reseaux.instagram" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Instagram.png" alt="Instagram" class="h-10" />
                            </a>
                            <a v-if="reseaux.youtube" :href="reseaux.youtube" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Youtube.png" alt="Youtube" class="h-10" />
                            </a>
                            <a v-if="reseaux.linkedin" :href="reseaux.linkedin" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Linkedin.png" alt="Linkedin" class="h-10" />
                            </a>
                            <a v-if="reseaux.tiktok" :href="reseaux.tiktok" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Tiktok.png" alt="Tiktok" class="h-10" />
                            </a>
                            <a v-if="reseaux.snapchat" :href="reseaux.snapchat" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Snapchat.png" alt="Snapchat" class="h-10" />
                            </a>
                            <a v-if="reseaux.whatsapp" :href="reseaux.whatsapp" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Whatsapp.png" alt="Whatsapp" class="h-10" />
                            </a>
                            <a v-if="reseaux.github" :href="reseaux.github" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Github.png" alt="Github" class="h-10" />
                            </a>
                            <a v-if="reseaux.twitter" :href="reseaux.twitter" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/X.png" alt="X" class="h-10" />
                            </a>
                            <a v-if="reseaux.spotify" :href="reseaux.spotify" target="_blank" class="inline-block">
                                <img src="@/assets/social-network-logo/Spotify.png" alt="Spotify" class="h-10" />
                            </a>
                            <!-- website -->
                            <a v-if="entreprise?.siteweb && entreprise.siteweb.trim() !== ''" :href="entreprise.siteweb"
                                target="_blank" class="text-black text-4xl">
                                <i class="fas fa-globe"></i>
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Colonne droite : Review + Horaires + Adresse + Carte (sticky) -->
                <div class="lg:col-span-4">
                    <div class="space-y-6 sticky top-6">

                        <!-- Note Google -->
                        <div v-if="googleReview.rating"
                            class="bg-white shadow-sm border border-gray-100 p-6 text-gray-800 text-left rounded-2xl">
                            <h2 class="text-2xl font-bold mb-2">Avis Google</h2>
                            <div class="flex items-center gap-2">
                                <!-- Étoiles précises -->
                                <template v-for="n in 5" :key="n">
                                    <span class="relative inline-block text-xl leading-none">
                                        <!-- Etoile vide (gris clair) -->
                                        <i class="fas fa-star text-gray-300"></i>
                                        <!-- Etoile pleine (jaune) masquée -->
                                        <i class="fas fa-star absolute top-0 left-0 text-transparent" :style="{
                                            background: `linear-gradient(90deg, #facc15 ${getStarFill(n, googleReview.rating)}%, transparent ${getStarFill(n, googleReview.rating)}%)`,
                                            WebkitBackgroundClip: 'text',
                                            backgroundClip: 'text'
                                        }">
                                        </i>
                                    </span>
                                </template>

                                <span class="text-gray-700 font-semibold">
                                    {{ googleReview.rating }} / 5 ({{ googleReview.total_reviews }} avis)
                                </span>
                            </div>
                        </div>


                        <!-- Horaires -->
                        <div v-if="horaires && hasAtLeastOneDayOpen"
                            class="bg-white shadow-sm border border-gray-100 p-6 text-gray-800 text-left rounded-2xl">
                            <h2 class="text-2xl font-bold mb-8">Horaires d'ouverture</h2>
                            <ul class="text-left divide-y divide-gray-200">
                                <li v-for="(jour, index) in jours" :key="index"
                                    class="flex justify-between items-center py-2">
                                    <strong class="capitalize"
                                        :class="{ 'font-extrabold': jour === currentDay, 'font-medium': jour !== currentDay }">
                                        {{ jour }}
                                    </strong>
                                    <span class="text-right">
                                        <template v-if="horaires[jour]?.ouvert">
                                            <template v-if="hasAnyHoraire(horaires[jour])">
                                                <template v-if="shouldDisplaySingleRange(horaires[jour])">
                                                    {{ formatHeure(horaires[jour].ouverture_matin) }} - {{
                                                        formatHeure(horaires[jour].fermeture_apresmidi) }}
                                                </template>
                                                <template v-else>
                                                    <span
                                                        v-if="horaires[jour].ouverture_matin && horaires[jour].fermeture_matin">
                                                        {{ formatHeure(horaires[jour].ouverture_matin) }} - {{
                                                            formatHeure(horaires[jour].fermeture_matin) }}
                                                    </span>
                                                    <span
                                                        v-if="horaires[jour].ouverture_apresmidi && horaires[jour].fermeture_apresmidi">
                                                        <br /> {{ formatHeure(horaires[jour].ouverture_apresmidi) }}
                                                        -
                                                        {{ formatHeure(horaires[jour].fermeture_apresmidi) }}
                                                    </span>
                                                </template>
                                            </template>
                                            <template v-else>
                                                <span class="text-gray-800 font-semibold">Ouvert</span>
                                            </template>
                                        </template>
                                        <span v-else class="text-gray-400 font-semibold">Fermé</span>
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <!-- Adresse + Carte -->
                        <div v-if="entreprise?.adresse && entreprise?.code_postal && entreprise?.ville"
                            class="bg-white shadow-sm border border-gray-100 p-6 text-gray-800 text-left rounded-2xl">
                            <h2 class="text-2xl font-bold mb-2">Adresse</h2>
                            <p class="text-lg mb-4 flex items-start">
                                <i class="fas fa-location-dot mt-1"></i>
                                <span class="ml-2">
                                    <a :href="`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(entreprise.adresse + ' ' + entreprise.code_postal + ' ' + entreprise.ville)}`"
                                        target="_blank" rel="noopener noreferrer" class="underline hover:text-gray-600">
                                        {{ entreprise.adresse }},
                                        <strong>{{ entreprise.code_postal }} {{ entreprise.ville }}</strong>
                                    </a>
                                </span>
                            </p>
                            <iframe :src="googleMapsEmbedUrl" width="100%" height="150" class="rounded-xl"
                                style="border:0;" allowfullscreen="" loading="lazy"
                                referrerpolicy="no-referrer-when-downgrade">
                            </iframe>
                        </div>
                    </div>
                </div>


                <!-- Réseaux sociaux -->
                <div v-if="hasSocialLinks"
                    class="block md:hidden p-6 text-center text-gray-800 bg-white shadow-sm border border-gray-100 rounded-2xl">
                    <h2 class="text-2xl font-bold text-gray-800">Suivez-nous</h2>
                    <div class="flex flex-wrap justify-center gap-6 text-4xl py-4">
                        <a v-if="reseaux.facebook" :href="reseaux.facebook" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Facebook.png" alt="Facebook" class="h-10" />
                        </a>
                        <a v-if="reseaux.instagram" :href="reseaux.instagram" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Instagram.png" alt="Instagram" class="h-10" />
                        </a>
                        <a v-if="reseaux.youtube" :href="reseaux.youtube" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Youtube.png" alt="Youtube" class="h-10" />
                        </a>
                        <a v-if="reseaux.linkedin" :href="reseaux.linkedin" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Linkedin.png" alt="Linkedin" class="h-10" />
                        </a>
                        <a v-if="reseaux.tiktok" :href="reseaux.tiktok" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Tiktok.png" alt="Tiktok" class="h-10" />
                        </a>
                        <a v-if="reseaux.snapchat" :href="reseaux.snapchat" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Snapchat.png" alt="Snapchat" class="h-10" />
                        </a>
                        <a v-if="reseaux.whatsapp" :href="reseaux.whatsapp" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Whatsapp.png" alt="Whatsapp" class="h-10" />
                        </a>
                        <a v-if="reseaux.github" :href="reseaux.github" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Github.png" alt="Github" class="h-10" />
                        </a>
                        <a v-if="reseaux.twitter" :href="reseaux.twitter" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/X.png" alt="X" class="h-10" />
                        </a>
                        <a v-if="reseaux.spotify" :href="reseaux.spotify" target="_blank" class="inline-block">
                            <img src="@/assets/social-network-logo/Spotify.png" alt="Spotify" class="h-10" />
                        </a>
                        <!-- website -->
                        <a v-if="entreprise?.siteweb && entreprise.siteweb.trim() !== ''" :href="entreprise.siteweb"
                            target="_blank" class="text-black text-4xl">
                            <i class="fas fa-globe"></i>
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <!-- Watermark -->
        <!-- <a href="/" target="_blank">
            <img src="@/assets/logo.png" alt="My Profolia" class="fixed bottom-4 right-4 opacity-100 h-6 md:h-12 z-0" />
        </a> -->
    </section>
</template>

<script setup>
// Importation des fonctions de composition Vue
import { ref, onMounted, computed, reactive } from 'vue'
import { useRouter } from 'vue-router';
const router = useRouter();
const token = localStorage.getItem('token')

const openDescription = ref(null)

function toggleDescription(index) {
    openDescription.value = openDescription.value === index ? null : index
}

// URL de base pour les appels à l'API backend
const baseUrl = process.env.VUE_APP_BASE_URL

// Liste des jours de la semaine utilisée pour afficher les horaires
const jours = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche']

// Objet réactif pour stocker les horaires d'ouverture
const horaires = reactive({})

// Stocke les informations de l'entreprise
const entreprise = ref(null)

// Stocke l'ID de l'entreprise pour les requêtes API
const entrepriseId = ref(null);

const prestations = ref([]);

// Jour actuel
const currentDay = ref(jours[(new Date().getDay() + 6) % 7])

// Stocke les liens vers les réseaux sociaux de l'entreprise
const reseaux = ref({
    facebook: null,
    instagram: null,
    linkedin: null,
    tiktok: null,
    whatsapp: null,
    youtube: null,
    snapchat: null,
    github: null,
    spotify: null,
    twitter: null
})

const googleReview = ref({
    google_map_url: '',
    rating: '',
    total_reviews: '',
    modified_at: ''
})

// Savoir si description est affichée
const hasDescription = computed(() => {
    return entreprise.value?.description && entreprise.value?.description.trim().length > 0;
});

const hasPrestations = computed(() => {
    return prestations.value && prestations.value.length > 0;
});

const hasSocialLinks = computed(() => {
    const hasReseaux = Object.values(reseaux.value).some(
        link => typeof link === "string" && link.trim() !== ""
    )
    const hasSiteweb =
        typeof entreprise.value?.siteweb === "string" &&
        entreprise.value.siteweb.trim() !== ""

    return hasReseaux || hasSiteweb
})

function getStarFill(starIndex, rating) {
    const full = Math.floor(rating);
    const decimal = rating - full;
    if (starIndex <= full) return 100;      // pleine
    if (starIndex === full + 1) return Math.round(decimal * 100); // partielle
    return 0;                               // vide
}

// Vérifie si un seul créneau horaire doit être affiché
// Cas particulier : ouverture le matin, fermeture l'après-midi uniquement
const shouldDisplaySingleRange = (h) => {
    return (
        h.ouverture_matin &&
        h.fermeture_apresmidi &&
        !h.fermeture_matin &&
        !h.ouverture_apresmidi
    )
}

// Vérifie si l'entreprise a au moins un jour d'ouverture
const hasAtLeastOneDayOpen = computed(() => {
    return Object.values(horaires).some(h => h?.ouvert);
});

// Vérifie si l'entreprise a des horaires d'ouverture
const hasAnyHoraire = (h) => {
    return (
        h.ouverture_matin || h.fermeture_matin ||
        h.ouverture_apresmidi || h.fermeture_apresmidi
    );
};


// Calcule l'URL complète du logo : s'il commence par "http", on le garde tel quel,
// sinon on le préfixe avec l'URL de base du backend
const logoFullUrl = computed(() => {
    if (!entreprise.value || !entreprise.value.logo_url) return ''
    return entreprise.value.logo_url.startsWith('http')
        ? entreprise.value.logo_url
        : `${baseUrl}${entreprise.value.logo_url.startsWith('/') ? '' : '/'}${entreprise.value.logo_url}`
})

// Formate une heure en supprimant les secondes ("08:00:00" => "08:00")
function formatHeure(heure) {
    if (!heure) return ''
    return heure.slice(0, 5)
}

async function fetchEntrepriseId() {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}/api/entreprises/user`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });

        if (!response.ok) {
            throw new Error(`Erreur HTTP ${response.status}`);
        }

        const data = await response.json();
        entrepriseId.value = data.id;
    } catch (error) {
        console.error('Erreur lors de la récupération de l’ID entreprise :', error);
    }
}

// Fonction exécutée au montage du composant (chargement initial)
onMounted(async () => {
    try {
        await fetchEntrepriseId(); // attends que entrepriseId.value soit défini

        if (!entrepriseId.value) {
            // Pas d'ID, on redirige vers NotFound ou une autre page
            router.replace({ name: 'NotFound' });
            return;
        }

        // 1. Récupérer l'entreprise via ID
        const headers = {
            'Authorization': `Bearer ${token}`
        };

        const entrepriseRes = await fetch(`${baseUrl}/api/entreprises/${entrepriseId.value}`, { headers });

        if (entrepriseRes.status === 404) {
            router.replace({ name: 'NotFound' });
            return;
        }
        if (!entrepriseRes.ok) throw new Error("Échec récupération entreprise");

        entreprise.value = await entrepriseRes.json();

        // 2. Requêtes parallèles pour les réseaux et horaires avec l'entrepriseId obtenu
        const [reseauxRes, horairesRes, prestationsRes, googleReviewRes] = await Promise.all([
            fetch(`${baseUrl}/api/reseaux/${entrepriseId.value}`),
            fetch(`${baseUrl}/api/horaires/${entrepriseId.value}`),
            fetch(`${baseUrl}/api/prestations/${entrepriseId.value}`),
            fetch(`${baseUrl}/api/google-review/${entrepriseId.value}`)

        ]);

        if (!reseauxRes.ok) throw new Error("Échec récupération réseaux sociaux");
        if (!horairesRes.ok) throw new Error("Échec récupération horaires");
        if (!prestationsRes.ok) throw new Error("Échec récupération horaires");
        if (!googleReviewRes.ok) throw new Error("Échec récupération google review");

        reseaux.value = await reseauxRes.json();
        const horairesData = await horairesRes.json();
        prestations.value = await prestationsRes.json();
        googleReview.value = await googleReviewRes.json();

        const horairesMap = {};
        for (const item of horairesData) {
            horairesMap[item.jour.toLowerCase()] = {
                ouvert: item.ouvert,
                ouverture_matin: item.ouverture_matin,
                fermeture_matin: item.fermeture_matin,
                ouverture_apresmidi: item.ouverture_apresmidi,
                fermeture_apresmidi: item.fermeture_apresmidi
            };
        }

        for (const jour of jours) {
            horaires[jour] = horairesMap[jour] || {
                ouvert: false,
                ouverture_matin: null,
                fermeture_matin: null,
                ouverture_apresmidi: null,
                fermeture_apresmidi: null
            };
        }

        if (entreprise.value?.nom) {
            document.title = `${entreprise.value.nom} – MyProfolia`;
        }

        document.documentElement.style.setProperty('--primary-color', entreprise.value.couleur || '#8b5cf6')

    } catch (error) {
        console.error('Erreur lors du chargement des données :', error);
        router.replace({ name: 'NotFound' });
    }
});


// Génère dynamiquement une URL d'iframe Google Maps à partir de l'adresse complète
const googleMapsEmbedUrl = computed(() => {
    if (!entreprise.value?.adresse || !entreprise.value?.ville || !entreprise.value?.code_postal) return ''
    const fullAddress = `${entreprise.value.adresse}, ${entreprise.value.code_postal} ${entreprise.value.ville}`
    const encodedAddress = encodeURIComponent(fullAddress)
    return `https://www.google.com/maps?q=${encodedAddress}&output=embed`
})
</script>
